/**
 * MODAL: EDIT CONTACT PRODUCTS
 * Modal pour éditer les produits associés à un contact avec protection des produits liés aux équipages
 */

import { useState, useEffect } from 'react';
import { Lock } from 'lucide-react';
import { useProductsSelect } from '../../hooks/useProductsSelect';
import { useCrews } from '../../hooks/useCrews';
import { Checkbox } from '../ui/checkbox';
import { Modal, ModalFooter } from '../ui/modal';

interface EditContactProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactId: string;
  currentProducts: string[];
  onSave: (productIds: string[]) => Promise<void>;
}

export function EditContactProductsModal({
  isOpen,
  onClose,
  contactId,
  currentProducts,
  onSave,
}: EditContactProductsModalProps) {
  const { products } = useProductsSelect();
  const { getCrewsByContactId } = useCrews();
  const [selectedProducts, setSelectedProducts] = useState<string[]>(currentProducts);
  const [lockedProductIds, setLockedProductIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Charger les équipages du contact pour identifier les produits verrouillés
  useEffect(() => {
    async function loadCrewProducts() {
      if (!contactId || !isOpen) return;

      const crews = await getCrewsByContactId(contactId);
      const productIds = crews
        .map((crew) => crew.product_id)
        .filter((id): id is string => id !== null && id !== undefined);

      setLockedProductIds(productIds);
    }

    loadCrewProducts();
  }, [contactId, isOpen, getCrewsByContactId]);

  // Réinitialiser la sélection quand la modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setSelectedProducts(currentProducts);
    }
  }, [isOpen, currentProducts]);

  const handleClose = () => {
    setSelectedProducts(currentProducts);
    onClose();
  };

  const toggleProduct = (productId: string, checked: boolean) => {
    // Ne pas permettre de décocher un produit verrouillé
    if (lockedProductIds.includes(productId) && !checked) {
      return;
    }

    setSelectedProducts((prev) =>
      checked ? [...prev, productId] : prev.filter((id) => id !== productId)
    );
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSave(selectedProducts);
      onClose();
    } catch (error) {
      console.error('Error saving products:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasLockedProducts = lockedProductIds.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Modifier les produits associés"
      size="md"
      footer={
        <ModalFooter
          onCancel={handleClose}
          onSubmit={handleSubmit}
          submitLabel="Enregistrer"
          isSubmitting={isSubmitting}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {/* Message d'information si des produits sont verrouillés */}
        {hasLockedProducts && (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <Lock className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700">
              Les produits liés à vos équipages ne peuvent pas être retirés
            </p>
          </div>
        )}

        {/* Liste des produits */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-zinc-900">Sélectionnez les produits</label>
          <div className="max-h-60 overflow-y-auto p-3 border border-zinc-200 rounded-md bg-zinc-50">
            {products.length === 0 ? (
              <p className="text-sm text-zinc-500">Aucun produit disponible</p>
            ) : (
              <div className="flex flex-col gap-2">
                {products.map((product) => {
                  const isLocked = lockedProductIds.includes(product.id);
                  const isChecked = selectedProducts.includes(product.id) || isLocked;

                  return (
                    <div
                      key={product.id}
                      className={`flex items-center gap-2 p-2 rounded transition-colors ${
                        isLocked ? 'bg-blue-50' : 'hover:bg-zinc-100'
                      }`}
                    >
                      <Checkbox
                        checked={isChecked}
                        onChange={(checked) => toggleProduct(product.id, checked)}
                        disabled={isLocked}
                      />
                      <span
                        className={`text-sm flex-1 ${
                          isLocked
                            ? 'text-blue-900 font-medium cursor-not-allowed'
                            : 'text-zinc-900 cursor-pointer'
                        }`}
                        onClick={() => {
                          if (!isLocked) {
                            toggleProduct(product.id, !isChecked);
                          }
                        }}
                      >
                        {product.name}
                      </span>
                      {isLocked && (
                        <div className="flex items-center gap-1 text-xs text-blue-600">
                          <Lock className="h-3 w-3" />
                          <span>Lié à un équipage</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
